import React, { useEffect } from 'react';
import { Card, CardBody, CardHeader, Badge, Progress, Row, Col, Button } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { fetchJokerSettings } from '../../slices/lt/joker/thunk';
import { setEditMode } from '../../slices/lt/joker/reducer';
import { formatDateTimeDDMMYYYY12Hour } from '../../helpers/dateUtils';

const JokerStatusCard = () => {
    const dispatch = useDispatch();
    const { currentSession, loading } = useSelector(state => state.lt.joker || {});

    const handleEdit = () => {
        if (currentSession) {
            dispatch(setEditMode(currentSession));
        }
    };

    useEffect(() => {
        // Fetch running session/settings once (prevents duplicate /api/joker?status=running calls)
        dispatch(fetchJokerSettings());
    }, [dispatch]);

    // If no session is running and not loading, we might want to hide or show "No Active Session"
    if (!currentSession) {
        if (loading) return <Card><CardBody>Loading...</CardBody></Card>;
        return (
            <Card>
                <CardHeader><h4 className="card-title mb-0">Joker Status</h4></CardHeader>
                <CardBody><p className="text-muted">No active joker session.</p></CardBody>
            </Card>
        );
    }

    const { status, totalJokerRounds, triggeredJokerRounds, multiplier, startTime, endTime } = currentSession;

    // Logic
    const total = totalJokerRounds || 0;
    const triggered = triggeredJokerRounds || 0;
    const remaining = Math.max(0, total - triggered);
    const percentage = total > 0 ? (triggered / total) * 100 : 0;

    const formatDate = (dateString) => {
        return formatDateTimeDDMMYYYY12Hour(dateString);
    };

    const timeRange = (startTime && endTime) ? `${formatDate(startTime)} - ${formatDate(endTime)}` : '-';

    // Status Badge Logic
    const isRunning = status?.toLowerCase() === 'running';
    const isPending = status?.toLowerCase() === 'pending';
    let badgeColor = "danger"; // Default: Red for Expire/Others
    if (isRunning) badgeColor = "primary"; // Blue for Running
    else if (isPending) badgeColor = "warning"; // Orange/Yellow for Pending

    return (
        <Card className="card-height-100">
            <CardHeader className="align-items-center d-flex">
                <h4 className="card-title mb-0 flex-grow-1">Joker Status</h4>
                <div className="flex-shrink-0 d-flex align-items-center gap-2">
                    <Button
                        color="link"
                        size="sm"
                        onClick={handleEdit}
                        className="p-0"
                        title="Edit Joker"
                    >
                        <i className="mdi mdi-pencil font-size-16"></i>
                    </Button>
                    <Badge color={badgeColor} className="fs-12">
                        {status ? status.toUpperCase() : "UNKNOWN"}
                    </Badge>
                </div>
            </CardHeader>
            <CardBody>
                <Row className="mb-3">
                    <div className="col-6">
                        <p className="text-muted mb-1 text-uppercase fw-medium fs-12">Time</p>
                        <h5 className="fs-14 mb-0">{timeRange}</h5>
                    </div>
                    <div className="col-6 text-end">
                        <p className="text-muted mb-1 text-uppercase fw-medium fs-12">Multiplier</p>
                        <Badge color="warning" className="fs-12 badge-border">{multiplier}x</Badge>
                    </div>
                </Row>

                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                        <p className="text-muted mb-1 text-uppercase fw-medium fs-12">Total Rounds</p>
                        <h5 className="fs-14 mb-0">{total}</h5>
                    </div>
                    <div className="text-end">
                        <p className="text-muted mb-1 text-uppercase fw-medium fs-12">Completed</p>
                        <h5 className="fs-14 mb-0">{triggered}</h5>
                    </div>
                </div>

                <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0 fs-13 text-uppercase fw-medium text-muted">Progress</h6>
                        <span className="badge bg-success-subtle text-success">{Math.round(percentage)}%</span>
                    </div>
                    <Progress value={percentage} color="success" className="animated-progess custom-progress progress-sm" />
                    <div className="mt-2 text-muted fs-12">
                        <span className="fw-bold text-dark">{remaining}</span> rounds remaining
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

export default JokerStatusCard;
